import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import { styled } from '@mui/system';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { AppointmentResponseDto, AppointmentStatus } from '../../types/appointment.types';
import { UserAvatar } from '../common/UserAvatar';

const STATUS_CONFIG: Record<AppointmentStatus, { label: string; color: 'warning' | 'success' | 'error' | 'default' }> = {
  [AppointmentStatus.PENDING]:   { label: 'Очікує',       color: 'warning' },
  [AppointmentStatus.CONFIRMED]: { label: 'Підтверджено', color: 'success' },
  [AppointmentStatus.CANCELLED]: { label: 'Скасовано',   color: 'error'   },
  [AppointmentStatus.COMPLETED]: { label: 'Завершено',   color: 'default' },
};

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString('uk-UA', { hour: '2-digit', minute: '2-digit' });

const StyledPaper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '16px',
  gap: '16px',
  width: '100%',
  background: '#FFFFFF',
  boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.05)',
  borderRadius: '12px',
}));

interface Props {
  appointments: AppointmentResponseDto[];
}

export const DashboardAppointmentsWidget: React.FC<Props> = ({ appointments }) => {
  return (
    <StyledPaper>
      <Box display="flex" alignItems="center" gap={1}>
        <HandshakeIcon color="primary" />
        <Typography variant="h6" fontWeight={600}>Зустрічі</Typography>
      </Box>

      {appointments.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 3, color: 'text.secondary' }}>
          <HandshakeIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body2">На цей день зустрічей немає</Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          {appointments.map((appt, index) => {
            const status = STATUS_CONFIG[appt.status] ?? { label: appt.status, color: 'default' };
            return (
              <React.Fragment key={appt.id}>
                {index > 0 && <Divider sx={{ my: 1 }} />}
                <Box display="flex" alignItems="center" gap={1.5} py={0.5}>
                  <UserAvatar
                    path={appt.expert.avatar ?? null}
                    name={appt.expert.name}
                    sx={{ width: 36, height: 36, flexShrink: 0 }}
                  />
                  <Box flexGrow={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {appt.expert.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(appt.scheduledAt)} · {appt.durationMinutes} хв
                    </Typography>
                  </Box>
                  <Chip
                    label={status.label}
                    color={status.color}
                    size="small"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </StyledPaper>
  );
};
